import type { Character } from '../schemas/character';
import type {
  DamageDie,
  Follower,
  FollowerHpRule,
  FollowerSkill,
  FollowerStats,
  FollowerTrait,
} from '../schemas';

/**
 * Derivazione delle statistiche effettive di un Follower (FI-8a).
 *
 * Il `Follower` memorizza valori base (`Follower.stats`) e traits modulari
 * (`Follower.traits`). Le statistiche **runtime effettive** (PF max,
 * dado di Danno post-trait, Carico effettivo, Armatura) sono **derivate**
 * via questo service e applicate dai consumer (UI, gioco live).
 *
 * Pattern coerente con `services/characterCreation.ts::deriveStartingDerivedStats`
 * per il PG.
 */

/**
 * Stats derivate runtime di un Follower. Output di {@link computeFollowerStats}.
 */
export interface ComputedFollowerStats {
  /** PF massimi calcolati dalla `hpRule` + traits `hpDelta`. */
  maxHp: number;
  /** Dado di Danno effettivo (eventuale override dal trait). */
  damageDie: DamageDie;
  /** Carico effettivo (base + traits `loadDelta`, capato a `caps.loadMax`). */
  load: number | 'narrative';
  /** Armatura derivata (somma di `traits.armorDelta`, capata a `caps.armorMax`). */
  armor: number;
}

const DAMAGE_DIE_ORDER: readonly DamageDie[] = ['d4', 'd6', 'd8', 'd10'];

function indexOfDie(die: DamageDie): number {
  const idx = DAMAGE_DIE_ORDER.indexOf(die);
  if (idx < 0) throw new Error(`damageDie sconosciuto: ${die}`);
  return idx;
}

/**
 * Bonus PF scalante derivato da una peculiarità di `kind: 'hp-bonus-scaling'`
 * (D8a — *Supporto Elementale* Druido «resistente»: `{ perCharacterLevel: 2,
 * max: 20 }`; *Animare Zombi* Sciamano idem). Computato da
 * `applyPeculiarities` in `followerPeculiarities.ts` e passato qui come
 * parametro opzionale.
 */
export interface PeculiarityHpBonusScaling {
  perCharacterLevel: number;
  max: number;
}

/**
 * Calcola i PF massimi del Follower applicando la `hpRule` + eventuali
 * `hpDelta` da traits + bonus scalante dalle peculiarità.
 *
 * - `fixed.value`: PF fissi (es. Famiglio = 5).
 * - `byLevel`: 5 / 10 / 15 per livelli 1 / 2 / 3 (manuale rr. 1290-1296).
 * - `scaling`: `base + perCharacterLevel × characterLevel`, clampato a `max`.
 *
 * **D8a**: `peculiarityBonusScaling` (opzionale) aggiunge un bonus
 * scalante al livello del PG, **cappato al suo proprio `max`** (assoluto:
 * un PG L20 con peculiarità `{perCharacterLevel: 2, max: 20}` ottiene +20,
 * non +40). Sommato DOPO il clamp della `hpRule.scaling` e dei `hpDelta`
 * dei traits — è un bonus indipendente.
 *
 * Composizione finale: `baseMax (da hpRule) + bonusScaling (capped) + hpDelta (da traits)`.
 */
export function computeFollowerMaxHp(
  hpRule: FollowerHpRule,
  characterLevel: number,
  traits: readonly FollowerTrait[],
  peculiarityBonusScaling?: PeculiarityHpBonusScaling,
): number {
  let baseMax: number;
  switch (hpRule.type) {
    case 'fixed':
      baseMax = hpRule.value;
      break;
    case 'byLevel':
      // Il livello del gregario non lo abbiamo qui; assumiamo che il chiamante
      // usi `byLevel` con uno stato consistente: il valore va ricavato dal
      // livello del gregario. Per ora restituiamo 5/10/15 in base al
      // `characterLevel` come fallback — verrà raffinato se necessario in
      // PR successive. Nota: nessun template DWR attualmente usa `byLevel`
      // — i template (Famiglio, Supporto, Compagno, Braccio Destro, Banda,
      // Animare Zombi) usano `fixed` o `scaling`.
      baseMax = 5;
      break;
    case 'scaling': {
      const computed = hpRule.base + hpRule.perCharacterLevel * characterLevel;
      baseMax = hpRule.max !== undefined ? Math.min(computed, hpRule.max) : computed;
      break;
    }
  }
  const hpDelta = traits.reduce((sum, t) => sum + (t.effects?.hpDelta ?? 0), 0);
  const peculiarityBonus = peculiarityBonusScaling
    ? Math.min(
        peculiarityBonusScaling.max,
        peculiarityBonusScaling.perCharacterLevel * characterLevel,
      )
    : 0;
  return baseMax + hpDelta + peculiarityBonus;
}

/**
 * Determina il dado di Danno effettivo del Follower applicando il primo
 * `damageDieOverride` trovato nei traits (semantica «sovrascrive», non
 * «aumenta di categoria»; per *feroce* del Compagno Animale il template
 * specifica direttamente `damageDieOverride: 'd8'`). Applica poi il cap
 * `caps.damageDieMax` se definito.
 */
export function computeFollowerDamageDie(
  baseDie: DamageDie,
  traits: readonly FollowerTrait[],
  capMax: DamageDie | undefined,
): DamageDie {
  let current = baseDie;
  for (const trait of traits) {
    const override = trait.effects?.damageDieOverride;
    if (override !== undefined) current = override;
  }
  if (capMax !== undefined && indexOfDie(current) > indexOfDie(capMax)) {
    current = capMax;
  }
  return current;
}

/**
 * Carico effettivo: base + somma dei `loadDelta` dai traits, eventualmente
 * capato a `caps.loadMax`. Se il base è `'narrative'`, ritorna `'narrative'`
 * (Banda di Guerra del Barbaro).
 */
export function computeFollowerLoad(
  baseLoad: number | 'narrative',
  traits: readonly FollowerTrait[],
  capMax: number | undefined,
): number | 'narrative' {
  if (baseLoad === 'narrative') return 'narrative';
  const delta = traits.reduce((sum, t) => sum + (t.effects?.loadDelta ?? 0), 0);
  const computed = baseLoad + delta;
  return capMax !== undefined ? Math.min(computed, capMax) : computed;
}

/**
 * Armatura effettiva: somma dei `armorDelta` dai traits, capata a `caps.armorMax`.
 * (Il manuale non assegna armatura "di base" al gregario — viene dalle
 * capacità utili o dall'equipaggiamento. L'equipaggiamento del Follower
 * sarà gestito in modalità "giocabile" — Fase 2 del progetto.)
 */
export function computeFollowerArmor(
  traits: readonly FollowerTrait[],
  capMax: number | undefined,
): number {
  const delta = traits.reduce((sum, t) => sum + (t.effects?.armorDelta ?? 0), 0);
  const computed = Math.max(0, delta);
  return capMax !== undefined ? Math.min(computed, capMax) : computed;
}

/**
 * Calcola tutte le stats derivate del Follower in un colpo solo. Pattern
 * coerente con `deriveStartingDerivedStats` del PG.
 */
export function computeFollowerStats(
  follower: Follower,
  character: Pick<Character, 'level'>,
): ComputedFollowerStats {
  const { stats, traits, caps, hpBonusScaling } = follower;
  return {
    // D8a: applica il bonus scaling memorizzato sul Follower (peculiarità "resistente").
    maxHp: computeFollowerMaxHp(stats.hpRule, character.level, traits, hpBonusScaling),
    damageDie: computeFollowerDamageDie(stats.damageDie, traits, caps?.damageDieMax),
    load: computeFollowerLoad(stats.load, traits, caps?.loadMax),
    armor: computeFollowerArmor(traits, caps?.armorMax),
  };
}

// ---------------------------------------------------------------------------
// Invarianti runtime
// ---------------------------------------------------------------------------

/**
 * Verifica gli invarianti runtime di un Follower (vincoli che lo schema Zod
 * non può esprimere perché coinvolgono campi cross). Lancia con messaggio
 * descrittivo al primo invariante violato.
 *
 * Invarianti:
 *  1. Ogni `skill.rank` ≤ `stats.level` (manuale r. 1344).
 *  2. Al massimo 1 `skill` con `isPrimary: true`.
 *  3. `currentHp ≥ 0` (già da schema, ridondante ma esplicito qui).
 */
export function assertFollowerInvariants(follower: Follower): void {
  // 1. Rank ≤ level
  for (const skill of follower.skills) {
    if (skill.rank > follower.stats.level) {
      throw new Error(
        `Follower '${follower.name}': Abilità ${skill.abilityId} a Rango ${skill.rank} > livello gregario ${follower.stats.level} (manuale r. 1344)`,
      );
    }
  }
  // 2. Primary unico
  const primaryCount = follower.skills.filter((s: FollowerSkill) => s.isPrimary).length;
  if (primaryCount > 1) {
    throw new Error(
      `Follower '${follower.name}': più di una Abilità marcata come primaria (${primaryCount})`,
    );
  }
}

// Re-export del tipo FollowerStats per chiarezza dell'API (alcuni consumer
// vorranno la differenza tra "stats base" e "stats derivate").
export type { FollowerStats };
